import * as React from "react";
import { BlockTitle } from "@blocks/Blocks";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import BtLuxeCarousel from "@components/Slider/BtLuxeCarousel";
import { BasicCardProps, CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import HomepageLifestyleStories from "@routes/Home/Lifestyle/HomepageLifestyleStories";
import type { NavigationItem } from "@typings/Navigation";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

type LifestyleProps = {
  /** Optional className for the component. */
  className?: string;
  /** The props needed for the lifestyle cards. */
  lifestyleCardProps: BasicCardProps[];
  /** The props needed for the carousel cards. */
  carouselCardProps: BasicCardProps[];
};

const section: NavigationItem = sectionNavigationItems["lifestyle"];

/**
 * Renders the Home - lifestyle section with a title, a list of articles, and a carousel.
 * https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1312-2352&t=g7P8QlYHW3Ymfii6-4
 */
const Lifestyle = ({
  lifestyleCardProps,
  carouselCardProps,
  className,
}: LifestyleProps): React.ReactElement => {
  const variant: CardVariations = {
    image: {
      width: "w-full lg:w-13/20",
      position: "left",
      aspectRatio: "aspect-3x2",
      imageClassName: "object-cover",
      orientation: "landscape",
      filters: {
        w: ImagePresets.XLARGE,
        h: 672,
        dpr: 1,
        f: "webp",
      },
    },
    content: {
      width: "w-full lg:w-7/20 text-white",
      extraClass:
        "lg:pl-4 justify-center items-center lg:items-start lg:justify-start mt-3 lg:mt-0",
      blurb: {
        extraClass: "max-lg:hidden font-public-sans text-xs font-normal mt-2",
      },
    },
    title: {
      size: cn("text-xl lg:text-6xl"),
      color: "text-white",
      extraClass: cn("text-center font-lct font-bold lg:text-left"),
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "break-words font-poppins text-4xs font-light leading-150",
    },
    subscriberLabel: {
      extraClass: "text-white pb-1",
    },
  };

  return (
    <section className={className} data-testid="homepage-lifestyle-component">
      <Row>
        <Column rootClassName="w-full lg:w-4/12 mb-6 mt-4">
          <BlockTitle
            link={section?.link}
            text={section.label?.toUpperCase()}
            rootLinkClassName="!text-white"
            rootClassName="pb-2"
          />

          <div className="stories">
            {lifestyleCardProps.map((cardProp) => (
              <HomepageLifestyleStories
                key={cardProp.id}
                cardProps={cardProp}
              />
            ))}
          </div>
        </Column>

        <Column rootClassName="w-full lg:w-8/12 mb-6 mt-4">
          <BtLuxeCarousel
            cardProps={carouselCardProps}
            variations={variant}
            dotsCustomClass="home-lifestyle-carousel-dots"
            btLuxeClass=""
          />
        </Column>
      </Row>
    </section>
  );
};

export default Lifestyle;
